/**
 * Nango integration service.
 * Handles connect sessions and the Nango Connect UI for OAuth flows.
 */
import Nango from '@nangohq/frontend';
import { auth } from './firebase';

const API_BASE = import.meta.env.VITE_API_URL || 'https://fs2.equosnine.com';

/**
 * Get authorization headers with current user's Firebase ID token.
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Request a Nango connect session token from the Go backend.
 * The backend calls Nango's API to create a session scoped to the business.
 */
export async function getConnectSession(
  provider: string,
  businessId: string,
): Promise<{ token: string; expiresAt: string }> {
  const response = await fetch(
    `${API_BASE}/api/integrations/connect-session?provider=${encodeURIComponent(provider)}&businessId=${encodeURIComponent(businessId)}`,
    { headers: await getAuthHeaders() },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create connect session' }));
    throw new Error(error.error || 'Failed to create connect session');
  }

  const data = await response.json();
  return { token: data.token, expiresAt: data.expiresAt };
}

/**
 * Open the Nango Connect UI modal for a given provider.
 * Returns a promise that resolves with the connection ID on success,
 * or rejects if the user closes the modal.
 */
export async function openNangoConnect(
  provider: string,
  businessId: string,
): Promise<{ connectionId: string; providerConfigKey: string }> {
  const session = await getConnectSession(provider, businessId);

  const nango = new Nango({ connectSessionToken: session.token });

  return new Promise((resolve, reject) => {
    nango.openConnectUI({
      sessionToken: session.token,
      onEvent: (event) => {
        if (event.type === 'connect') {
          resolve({
            connectionId: event.payload.connectionId,
            providerConfigKey: event.payload.providerConfigKey,
          });
        } else if (event.type === 'close') {
          reject(new Error('Connection cancelled by user'));
        }
      },
    });
  });
}

/**
 * Disconnect an integration via the Go backend.
 */
export async function disconnectIntegration(
  provider: string,
  businessId: string,
): Promise<void> {
  const response = await fetch(`${API_BASE}/api/integrations/disconnect`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ provider, businessId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to disconnect' }));
    throw new Error(error.error || 'Failed to disconnect integration');
  }
}
